import React, { useState } from 'react';
import { format } from 'date-fns';
import {
  Building2, Calendar, DollarSign, FileText,
  CheckCircle, XCircle, AlertTriangle, ArrowRight, X,
  Flag, Award, Users, MapPin, Settings, ChevronDown, ChevronUp,
  Truck, Package, Percent, Calculator
} from 'lucide-react';
import './MarkForAnalysis.css';

// Default analyst configuration values
const DEFAULT_CONFIG = {
  exchangeRate: 1.36,
  shippingOrigin: 'Shenzhen, China',
  shippingDestination: 'Vancouver, Canada',
  dutyRate: 0.05,
  defectAllowance: 0.03,
  warrantyAllowance: 0.02,
  marginBuffer: 0.15,
  quantities: '',
  analystNotes: ''
};

const MarkForAnalysis = ({ tender, onStartAnalysis, onCancel }) => {
  const [showConfig, setShowConfig] = useState(false);
  const [config, setConfig] = useState(DEFAULT_CONFIG);

  if (!tender) {
    return (
      <div className="mark-for-analysis">
        <div className="no-tender">
          <AlertTriangle size={48} />
          <h3>No Tender Selected</h3>
          <p>Please select a saved tender to begin analysis.</p>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const normalized = dateString.length === 10 ? dateString + 'T00:00:00' : dateString;
      return format(new Date(normalized), 'MMMM do, yyyy');
    } catch {
      return 'Invalid date';
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'TBD';
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getDaysUntilClose = (closingDate) => {
    if (!closingDate) return null;
    try {
      return Math.ceil((new Date(closingDate) - new Date()) / (1000 * 60 * 60 * 24));
    } catch {
      return null;
    }
  };

  const handleConfigChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleStartAnalysis = () => {
    // Pass config along with the analysis start
    onStartAnalysis(config);
  };

  const closingDate = tender.closing_date || tender.closingDate;
  const daysLeft = getDaysUntilClose(closingDate);
  const estimatedValue = tender.amount || tender.value?.amount;
  const organizationName = tender.organization?.contracting_entity ||
                          tender.organization?.name ||
                          tender.contracting_entity ||
                          'Government of Canada';

  // Extract BI indicators
  const indicators = [];

  if (tender['canadianMade-fabriquéCanadien'] === 'yes') {
    indicators.push({ label: 'Canadian Made Required', type: 'requirement', icon: Flag });
  }
  if (tender['experienceRequired-expérienceRequise'] === 'true' || tender['experienceRequired-expérienceRequise'] === true) {
    indicators.push({ label: 'Experience Required', type: 'requirement', icon: Award });
  }
  if (tender['indigenousBusinessRequired-entrepriseAutochtooneRequise'] === 'true' || tender['indigenousBusinessRequired-entrepriseAutochtooneRequise'] === true) {
    indicators.push({ label: 'Indigenous Business Preferred', type: 'preference', icon: Users });
  }

  const category = tender.procurementCategory || tender['masterCategory-catégoriePrincipale'] || tender.procurement_category || 'Unknown';
  const deliveryRegions = tender['deliveryRegions-régionsLivraison'] || [];

  return (
    <div className="mark-for-analysis">
      <div className="analysis-header">
        <div className="header-icon">
          <FileText size={32} />
        </div>
        <div className="header-text">
          <h3>Review Tender Details</h3>
          <p>Configure analysis parameters and start the 5-phase sourcing analysis</p>
        </div>
      </div>

      <div className="tender-summary-card">
        <div className="summary-header">
          <h4 className="tender-title">{tender.title || 'Untitled Tender'}</h4>
          {daysLeft !== null && (
            <span className={`days-badge ${daysLeft <= 3 ? 'urgent' : daysLeft <= 7 ? 'soon' : 'normal'}`}>
              {daysLeft <= 0 ? 'Closed' : `${daysLeft} days left`}
            </span>
          )}
        </div>

        <div className="summary-grid">
          <div className="summary-item">
            <Building2 size={18} className="item-icon" />
            <div className="item-content">
              <span className="item-label">Organization</span>
              <span className="item-value">{organizationName}</span>
            </div>
          </div>

          <div className="summary-item">
            <Calendar size={18} className="item-icon" />
            <div className="item-content">
              <span className="item-label">Closing Date</span>
              <span className="item-value">{formatDate(closingDate)}</span>
            </div>
          </div>

          <div className="summary-item">
            <DollarSign size={18} className="item-icon" />
            <div className="item-content">
              <span className="item-label">Estimated Value</span>
              <span className="item-value">{formatCurrency(estimatedValue)}</span>
            </div>
          </div>

          <div className="summary-item">
            <FileText size={18} className="item-icon" />
            <div className="item-content">
              <span className="item-label">Category</span>
              <span className="item-value">{category}</span>
            </div>
          </div>
        </div>

        {indicators.length > 0 && (
          <div className="indicators-section">
            <h5>Key Requirements</h5>
            <div className="indicators-list">
              {indicators.map((indicator, index) => {
                const IconComponent = indicator.icon;
                return (
                  <div key={index} className={`indicator-badge ${indicator.type}`}>
                    <IconComponent size={14} />
                    {indicator.label}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {deliveryRegions.length > 0 && (
          <div className="regions-section">
            <h5><MapPin size={14} /> Delivery Regions</h5>
            <div className="regions-list">
              {deliveryRegions.slice(0, 5).map((region, index) => (
                <span key={index} className="region-tag">{region}</span>
              ))}
              {deliveryRegions.length > 5 && (
                <span className="region-tag more">+{deliveryRegions.length - 5} more</span>
              )}
            </div>
          </div>
        )}

        {tender.description && (
          <div className="description-section">
            <h5>Description</h5>
            <p className="tender-description">
              {tender.description.length > 500
                ? tender.description.substring(0, 500) + '...'
                : tender.description
              }
            </p>
          </div>
        )}
      </div>

      {/* Analyst Configuration Section */}
      <div className="config-section">
        <button
          className="config-toggle"
          onClick={() => setShowConfig(!showConfig)}
        >
          <Settings size={18} />
          <span>Analyst Configuration</span>
          {showConfig ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>

        {showConfig && (
          <div className="config-panel">
            <div className="config-group">
              <h5><DollarSign size={16} /> Currency & Exchange</h5>
              <div className="config-row">
                <label>
                  <span>USD to CAD Rate</span>
                  <input
                    type="number"
                    step="0.01"
                    value={config.exchangeRate}
                    onChange={(e) => handleConfigChange('exchangeRate', parseFloat(e.target.value) || 1.36)}
                  />
                </label>
              </div>
            </div>

            <div className="config-group">
              <h5><Truck size={16} /> Shipping & Logistics</h5>
              <div className="config-row">
                <label>
                  <span>Origin (FOB Port)</span>
                  <select
                    value={config.shippingOrigin}
                    onChange={(e) => handleConfigChange('shippingOrigin', e.target.value)}
                  >
                    <option value="Shenzhen, China">Shenzhen, China</option>
                    <option value="Shanghai, China">Shanghai, China</option>
                    <option value="Ningbo, China">Ningbo, China</option>
                    <option value="Guangzhou, China">Guangzhou, China</option>
                    <option value="Hong Kong">Hong Kong</option>
                    <option value="Taiwan">Taiwan</option>
                    <option value="Vietnam">Vietnam</option>
                  </select>
                </label>
                <label>
                  <span>Destination Port</span>
                  <select
                    value={config.shippingDestination}
                    onChange={(e) => handleConfigChange('shippingDestination', e.target.value)}
                  >
                    <option value="Vancouver, Canada">Vancouver, Canada</option>
                    <option value="Toronto, Canada">Toronto, Canada</option>
                    <option value="Montreal, Canada">Montreal, Canada</option>
                    <option value="Halifax, Canada">Halifax, Canada</option>
                    <option value="Prince Rupert, Canada">Prince Rupert, Canada</option>
                  </select>
                </label>
              </div>
            </div>

            <div className="config-group">
              <h5><Percent size={16} /> Cost Factors</h5>
              <div className="config-row three-col">
                <label>
                  <span>Duty Rate (%)</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="1"
                    value={config.dutyRate}
                    onChange={(e) => handleConfigChange('dutyRate', parseFloat(e.target.value) || 0)}
                  />
                  <small>{(config.dutyRate * 100).toFixed(1)}%</small>
                </label>
                <label>
                  <span>Defect Allow. (%)</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="1"
                    value={config.defectAllowance}
                    onChange={(e) => handleConfigChange('defectAllowance', parseFloat(e.target.value) || 0)}
                  />
                  <small>{(config.defectAllowance * 100).toFixed(1)}%</small>
                </label>
                <label>
                  <span>Warranty Allow. (%)</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="1"
                    value={config.warrantyAllowance}
                    onChange={(e) => handleConfigChange('warrantyAllowance', parseFloat(e.target.value) || 0)}
                  />
                  <small>{(config.warrantyAllowance * 100).toFixed(1)}%</small>
                </label>
              </div>
              <div className="config-row">
                <label>
                  <span>Target Margin Buffer (%)</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="1"
                    value={config.marginBuffer}
                    onChange={(e) => handleConfigChange('marginBuffer', parseFloat(e.target.value) || 0.15)}
                  />
                  <small>{(config.marginBuffer * 100).toFixed(1)}%</small>
                </label>
              </div>
            </div>

            <div className="config-group">
              <h5><Package size={16} /> Quantity Assumptions</h5>
              <div className="config-row">
                <label className="full-width">
                  <span>Quantity Estimates (comma-separated, e.g., "100, 500, 1000")</span>
                  <input
                    type="text"
                    placeholder="Enter quantity tiers for pricing analysis..."
                    value={config.quantities}
                    onChange={(e) => handleConfigChange('quantities', e.target.value)}
                  />
                </label>
              </div>
            </div>

            <div className="config-group">
              <h5><Calculator size={16} /> Analyst Notes</h5>
              <div className="config-row">
                <label className="full-width">
                  <span>Additional Notes / Special Considerations</span>
                  <textarea
                    placeholder="E.g., competitor intelligence, special requirements, sourcing notes..."
                    value={config.analystNotes}
                    onChange={(e) => handleConfigChange('analystNotes', e.target.value)}
                    rows={3}
                  />
                </label>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="analysis-info">
        <h5>5-Phase Sourcing Analysis Pipeline</h5>
        <ul className="info-list phase-list">
          <li>
            <span className="phase-badge">0</span>
            <div className="phase-content">
              <strong>Assumptions & Scoring</strong>
              <span>Validate config, score tender viability (1-10)</span>
            </div>
          </li>
          <li>
            <span className="phase-badge">1</span>
            <div className="phase-content">
              <strong>Technical Extraction</strong>
              <span>Extract items, specs, quantities → Compliance Matrix</span>
            </div>
          </li>
          <li>
            <span className="phase-badge">2</span>
            <div className="phase-content">
              <strong>Canada Pricing Map</strong>
              <span>Retail & wholesale benchmarks from Canadian sources</span>
            </div>
          </li>
          <li>
            <span className="phase-badge">3</span>
            <div className="phase-content">
              <strong>OEM/China Sourcing</strong>
              <span>FOB quotes, landed cost build, margin analysis</span>
            </div>
          </li>
          <li>
            <span className="phase-badge">4</span>
            <div className="phase-content">
              <strong>Competitor Banding</strong>
              <span>Estimate competitor prices from historical data</span>
            </div>
          </li>
          <li>
            <span className="phase-badge">5</span>
            <div className="phase-content">
              <strong>Bid Pricing</strong>
              <span>Final bid price, arbitrage calculation, Bid/No-Bid</span>
            </div>
          </li>
        </ul>
      </div>

      <div className="action-buttons">
        <button className="btn-secondary" onClick={onCancel}>
          <X size={16} />
          Cancel
        </button>
        <button className="btn-primary" onClick={handleStartAnalysis}>
          Start Analysis
          <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default MarkForAnalysis;
